// Storage-agnostic cache helper.
//
// Consolidates the `{ data, timestamp }` + `Date.now()` TTL pattern that was hand-rolled
// (with subtly different envelopes and namespacing) across usePathwayCatalog, useSessionMilestones,
// modalQueue, and sessionInitializer. The cache never knows *what* storage backs it — callers inject
// anything implementing the 3-method `StorageLike` contract, which native `window.localStorage` /
// `window.sessionStorage` satisfy as-is. Every operation is best-effort: any storage failure is
// routed to the optional `onError` hook and degrades to a cache miss, never a throw.

export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export interface CacheOptions {
  /** Backing store — `window.localStorage`, `window.sessionStorage`, `memoryStorage()`, or any adapter. */
  storage: StorageLike;
  /** Prefix for every key, keeping entries scoped (e.g. per-feature, and pass a per-user key at call time). */
  namespace: string;
  /** Time-to-live in ms. Omit (or 0) for a durable entry that never expires. */
  ttlMs?: number;
  /** Schema version stamped into every entry; bumping it silently invalidates all older entries. */
  version?: number | string;
  /** Clock injection point for deterministic tests. Defaults to `Date.now`. */
  now?: () => number;
  /** Telemetry hook for storage failures. Defaults to swallowing (best-effort). */
  onError?: (error: unknown, op: 'get' | 'set' | 'remove') => void;
}

export interface Cache<T> {
  /** Return the cached value, or `null` on miss / expiry / version mismatch / storage error. */
  get(key: string): T | null;
  /** Store a value under the namespaced key, stamped with the current time and version. */
  set(key: string, value: T): void;
  /** Remove the namespaced key. */
  remove(key: string): void;
  /** True if a live (non-expired, current-version) entry exists. Evicts a stale entry as a side effect. */
  has(key: string): boolean;
  /** Read-through: return the cached value, else run `loader`, cache a non-nullish result, and return it. */
  getOrLoad(key: string, loader: () => Promise<T> | T): Promise<T>;
}

interface Envelope<T> {
  /** version */ v: number | string;
  /** timestamp (ms) */ t: number;
  /** data */ d: T;
}

const DEFAULT_VERSION = 1;

// `null` is reserved to mean "miss", so cached values must never legitimately be `null`
// (all current consumers store objects or `true`). Store `undefined`-free values only.
export function createCache<T = unknown>(options: CacheOptions): Cache<T> {
  const { storage, namespace, ttlMs, onError } = options;
  const version = options.version ?? DEFAULT_VERSION;
  const now = options.now ?? (() => Date.now());

  const fullKey = (key: string): string => `${namespace}:${key}`;

  const report = (error: unknown, op: 'get' | 'set' | 'remove'): void => {
    if (onError) onError(error, op);
  };

  const remove = (key: string): void => {
    try {
      storage.removeItem(fullKey(key));
    } catch (error) {
      report(error, 'remove');
    }
  };

  const get = (key: string): T | null => {
    try {
      const raw = storage.getItem(fullKey(key));
      if (raw == null) return null;
      const envelope = JSON.parse(raw) as Envelope<T>;
      // A superseded schema version, or a malformed envelope (non-number timestamp / absent
      // data) → treat as absent and evict. Without the shape check a valid-JSON-but-malformed
      // entry (e.g. `{ v: 1 }`) would pass the TTL test (`now() - undefined` is NaN, never > ttl)
      // and `get` would return `undefined`, making `has` falsely report a hit.
      if (
        !envelope ||
        envelope.v !== version ||
        typeof envelope.t !== 'number' ||
        !Object.prototype.hasOwnProperty.call(envelope, 'd')
      ) {
        remove(key);
        return null;
      }
      if (ttlMs && now() - envelope.t > ttlMs) {
        remove(key);
        return null;
      }
      return envelope.d;
    } catch (error) {
      report(error, 'get');
      return null;
    }
  };

  const set = (key: string, value: T): void => {
    try {
      const envelope: Envelope<T> = { v: version, t: now(), d: value };
      storage.setItem(fullKey(key), JSON.stringify(envelope));
    } catch (error) {
      report(error, 'set');
    }
  };

  const has = (key: string): boolean => get(key) !== null;

  const getOrLoad = async (key: string, loader: () => Promise<T> | T): Promise<T> => {
    const hit = get(key);
    if (hit !== null) return hit;
    const value = await loader();
    if (value !== null && value !== undefined) set(key, value);
    return value;
  };

  return { get, set, remove, has, getOrLoad };
}

// In-memory StorageLike — a fallback when Web Storage is unavailable, and a clean seam for tests.
export function memoryStorage(): StorageLike {
  const map = new Map<string, string>();
  return {
    getItem: (key) => (map.has(key) ? (map.get(key) as string) : null),
    setItem: (key, value) => {
      map.set(key, value);
    },
    removeItem: (key) => {
      map.delete(key);
    }
  };
}

export interface GuardOptions {
  /** Storage to use when `produce` throws, returns nullish, or fails the write-probe. */
  fallback?: StorageLike;
  /** Verify the store is writable with a throwaway set/remove. Turn off for write-costly or read-only adapters. */
  probe?: boolean;
}

// Wrap any storage factory so a construction throw (e.g. `window.localStorage` in private mode),
// a nullish result (SSR), or a failing write-probe degrades to a safe fallback instead of crashing.
// Storage-agnostic: works for Web Storage, a custom StorageLike adapter, anything.
export function guardedStorage(
  produce: () => StorageLike | null | undefined,
  { fallback = memoryStorage(), probe = true }: GuardOptions = {}
): StorageLike {
  try {
    const storage = produce();
    if (!storage) return fallback;
    if (probe) {
      const key = '__cache_probe__';
      storage.setItem(key, '1');
      storage.removeItem(key);
    }
    return storage;
  } catch {
    return fallback;
  }
}

// Convenience for the two browser cases: obtain Web Storage with the private-mode / SSR guard.
// Accessing `window.localStorage` can itself throw, so it goes through guardedStorage.
export function safeWebStorage(type: 'local' | 'session'): StorageLike {
  if (typeof window === 'undefined') return memoryStorage();
  return guardedStorage(() => (type === 'session' ? window.sessionStorage : window.localStorage));
}
